import { apiRequest } from '../core/http';
import type { OrganizationStatus } from '../access/access.types';

const ENDPOINT = '/v1/admin/organizations';

/** Code of the organization every unassigned user belongs to. */
export const DEFAULT_ORG_CODE = 'default';

/** Mirrors core's OrganizationResponseDto. */
export interface OrganizationDto {
  uuid: string;
  code: string;
  name: string;
  status: OrganizationStatus;
  cycleAnchorDay: number | null;
  memberCount: number;
  /** Live teams. */
  teamCount: number;
  /** Active org admins; zero means the organization has none. */
  orgAdminCount: number;
  /** ISO-8601 UTC */
  createdAt: string;
}

export interface OrganizationPage {
  data: OrganizationDto[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export interface OrganizationsParams extends Record<string, unknown> {
  search?: string;
  status?: OrganizationStatus;
  page?: number;
  limit?: number;
}

export interface CreateOrganizationBody {
  code: string;
  name: string;
  cycleAnchorDay: number;
}

/** The code is not part of the update: core refuses to change it. */
export interface UpdateOrganizationBody {
  name?: string;
  cycleAnchorDay?: number;
}

export function isDefaultOrganization(org: Pick<OrganizationDto, 'code'>): boolean {
  return org.code === DEFAULT_ORG_CODE;
}

export function listOrganizations(params: OrganizationsParams) {
  return apiRequest<OrganizationPage>('get', ENDPOINT, undefined, { params });
}

export function getOrganization(uuid: string) {
  return apiRequest<OrganizationDto>('get', `${ENDPOINT}/${uuid}`);
}

export function createOrganization(body: CreateOrganizationBody) {
  return apiRequest<OrganizationDto>('post', ENDPOINT, body);
}

export function updateOrganization(uuid: string, body: UpdateOrganizationBody) {
  return apiRequest<OrganizationDto>('patch', `${ENDPOINT}/${uuid}`, body);
}

export function suspendOrganization(uuid: string) {
  return apiRequest<OrganizationDto>('post', `${ENDPOINT}/${uuid}/suspend`);
}

export function activateOrganization(uuid: string) {
  return apiRequest<OrganizationDto>('post', `${ENDPOINT}/${uuid}/activate`);
}

export function deleteOrganization(uuid: string) {
  return apiRequest('delete', `${ENDPOINT}/${uuid}`);
}
